import React from 'react';
import styled from 'styled-components';

/**
 * TourExitWarning
 *
 * Confirmation dialog shown when the user attempts to leave the onboarding
 * tour from the checklist via a production-URL action. Reminds them they
 * can return via the floating "Incomplete setup" button.
 *
 * Pure presentation — parent owns state. Pattern:
 *   const [pendingExit, setPendingExit] = useState(null);
 *   ...
 *   <TourExitWarning
 *     open={Boolean(pendingExit)}
 *     onCancel={() => setPendingExit(null)}
 *     onConfirm={() => { const fn = pendingExit; setPendingExit(null); fn(); }}
 *   />
 */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 10010;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  padding: 28px 28px 22px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  font-family: 'IBM Plex Sans', Helvetica, Arial, sans-serif;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
`;

const Body = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: #4a4a4a;
  margin: 0 0 22px 0;
`;

const Highlight = styled.span`
  color: #7200E0;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
`;

const SecondaryButton = styled(Button)`
  background-color: #ffffff;
  color: #4a4a4a;
  border: 1px solid #d0d0d0;

  &:hover { background-color: #f5f5f5; }
`;

const PrimaryButton = styled(Button)`
  background-color: #7200E0;
  color: #ffffff;

  &:hover { background-color: #5d00b8; }
`;

// Copy per surface: the warning fires both mid-TOUR (guided walkthrough) and
// from the setup CHECKLIST (no tour running) — calling the checklist a "tour"
// confused users who never started one.
const COPY = {
    tour: {
        title: 'Leave the onboarding tour?',
        stay: 'Stay in tour',
        leave: 'Leave tour',
    },
    checklist: {
        title: 'Leave your setup?',
        stay: 'Keep setting up',
        leave: 'Leave setup',
    },
};

export default function TourExitWarning({ open, onCancel, onConfirm, variant = 'tour' }) {
    if (!open) return null;
    const copy = COPY[variant] || COPY.tour;

    return (
        <Overlay role="dialog" aria-modal="true" aria-labelledby="tour-exit-title">
            <Modal onClick={(e) => e.stopPropagation()}>
                <Title id="tour-exit-title">{copy.title}</Title>
                <Body>
                    Your setup isn't finished yet. You can come back to where you left off any
                    time by clicking the <Highlight>Setup Progress</Highlight> button in the
                    left sidebar.
                </Body>
                <Actions>
                    <SecondaryButton type="button" onClick={onCancel}>
                        {copy.stay}
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={onConfirm}>
                        {copy.leave}
                    </PrimaryButton>
                </Actions>
            </Modal>
        </Overlay>
    );
}
